import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { getTodayET } from '@/hooks/useAddDrop';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Eye, ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, Users, Swords,
  ChevronDown, Bomb, Shield, MessageCircle, Send, Radio,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

// ─── Constants ──────────────────────────────────────────────
const ADMIN_EMAILS = ['adamneilemerson@gmail.com', 'adam@sabotagefantasy.com'];

// ─── Types ──────────────────────────────────────────────────
interface League { id: string; name: string; status: string }
interface Team { id: string; name: string; user_id: string; owner_name: string }
interface Player { id: string; full_name: string; position_abbreviation: string; mlb_team_abbreviation: string }
interface RosterRow { id: string; player_id: string; slot: string; is_starter: boolean }
interface DailyRow { id: string; player_id: string; slot: string; is_starter: boolean; locked_at: string | null; game_date: string }
interface PmsRow {
  id: string; player_id: string; team_id: string | null; game_date: string;
  matchup_week: number; trash_points: number; anti_points: number;
  total_points: number; is_starter: boolean; matchup_id: string;
}
interface Matchup {
  id: string; week: number; home_team_id: string; away_team_id: string;
  home_score: number; away_score: number; status: string;
  week_start: string; week_end: string;
}
interface Sabotage {
  id: string; sabotage_name: string; sabotage_icon: string; sabotage_description: string;
  applied_by_team_id: string; target_team_id: string; week: number; status: string;
  applied_at: string; effect_config: any;
}
interface Mismatch {
  type: 'ghost' | 'missing' | 'slot_collision' | 'starter_mismatch';
  player_name: string;
  detail: string;
}

// ─── Component ──────────────────────────────────────────────
const AdminGodMode = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ADMIN_EMAILS.includes(user?.email ?? '');

  // Selectors
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  // Data
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [dailyLineups, setDailyLineups] = useState<DailyRow[]>([]);
  const [playerMap, setPlayerMap] = useState<Map<string, Player>>(new Map());
  const [matchup, setMatchup] = useState<Matchup | null>(null);
  const [opponentName, setOpponentName] = useState('');
  const [pmsRows, setPmsRows] = useState<PmsRow[]>([]);
  const [sabotagesSent, setSabotagesSent] = useState<Sabotage[]>([]);
  const [sabotagesReceived, setSabotagesReceived] = useState<Sabotage[]>([]);
  const [mismatches, setMismatches] = useState<Mismatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [teamNamesMap, setTeamNamesMap] = useState<Map<string, string>>(new Map());

  // Admin chat state
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');
  const [broadcastMode, setBroadcastMode] = useState(false);
  const [sendingChat, setSendingChat] = useState(false);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // ─── Auth gate ──────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/beta', { replace: true });
    }
  }, [authLoading, user, isAdmin, navigate]);

  // ─── Fetch leagues on mount ─────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { data } = await supabase
        .from('leagues')
        .select('id, name, status')
        .order('created_at', { ascending: false });
      if (data) {
        setLeagues(data);
        const f6p = data.find(l => l.name.toLowerCase().includes('f6p'));
        if (f6p) setSelectedLeagueId(f6p.id);
        else if (data.length > 0) setSelectedLeagueId(data[0].id);
      }
    })();
  }, [isAdmin]);

  // ─── Fetch teams when league changes ────────────────────
  useEffect(() => {
    if (!selectedLeagueId) return;
    setSelectedTeamId('');
    setRoster([]); setDailyLineups([]); setPmsRows([]);
    setMatchup(null); setSabotagesSent([]); setSabotagesReceived([]);
    setMismatches([]);
    (async () => {
      const { data: teamsData } = await supabase
        .from('teams')
        .select('id, name, user_id')
        .eq('league_id', selectedLeagueId)
        .order('name');
      if (!teamsData) return;

      const userIds = teamsData.map(t => t.user_id).filter(Boolean);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, username')
        .in('id', userIds);
      const pmap = new Map((profiles || []).map(p => [p.id, p.display_name || p.username || 'Unknown']));
      const nameMap = new Map<string, string>();

      const enriched = teamsData.map(t => {
        const owner = pmap.get(t.user_id) || 'Unknown';
        nameMap.set(t.id, t.name);
        return { ...t, owner_name: owner };
      });
      setTeams(enriched);
      setTeamNamesMap(nameMap);
    })();
  }, [selectedLeagueId]);

  // ─── Fetch team data when team is selected ──────────────
  const fetchTeamData = useCallback(async () => {
    if (!selectedTeamId || !selectedLeagueId) return;
    setLoading(true);

    const today = getTodayET();

    try {
      // 1. Roster assignments
      const { data: ra } = await supabase
        .from('roster_assignments')
        .select('id, player_id, slot, is_starter')
        .eq('team_id', selectedTeamId);

      // 2. Daily lineups for today
      const { data: dl } = await supabase
        .from('daily_lineups')
        .select('id, player_id, slot, is_starter, locked_at, game_date')
        .eq('team_id', selectedTeamId)
        .eq('game_date', today);

      // 3. Current matchup (week_start <= today <= week_end)
      const { data: matchups } = await supabase
        .from('matchups')
        .select('id, week, home_team_id, away_team_id, home_score, away_score, status, week_start, week_end')
        .eq('league_id', selectedLeagueId)
        .or(`home_team_id.eq.${selectedTeamId},away_team_id.eq.${selectedTeamId}`)
        .lte('week_start', today)
        .gte('week_end', today)
        .limit(1);
      const currentMatchup = matchups?.[0] || null;

      // 4. Player match scores for this matchup period
      let pms: PmsRow[] = [];
      if (currentMatchup) {
        const { data } = await supabase
          .from('player_match_scores')
          .select('id, player_id, team_id, game_date, matchup_week, trash_points, anti_points, total_points, is_starter, matchup_id')
          .eq('league_id', selectedLeagueId)
          .eq('matchup_week', currentMatchup.week)
          .order('game_date');
        pms = (data || []) as PmsRow[];
      }

      // 5. Sabotages for current week
      let sentSab: Sabotage[] = [];
      let recvSab: Sabotage[] = [];
      if (currentMatchup) {
        const [s1, s2] = await Promise.all([
          supabase.from('applied_sabotages')
            .select('id, sabotage_name, sabotage_icon, sabotage_description, applied_by_team_id, target_team_id, week, status, applied_at, effect_config')
            .eq('league_id', selectedLeagueId)
            .eq('applied_by_team_id', selectedTeamId)
            .eq('week', currentMatchup.week),
          supabase.from('applied_sabotages')
            .select('id, sabotage_name, sabotage_icon, sabotage_description, applied_by_team_id, target_team_id, week, status, applied_at, effect_config')
            .eq('league_id', selectedLeagueId)
            .eq('target_team_id', selectedTeamId)
            .eq('week', currentMatchup.week),
        ]);
        sentSab = (s1.data || []) as Sabotage[];
        recvSab = (s2.data || []) as Sabotage[];
      }

      // 6. Fetch player info for all referenced player IDs
      const allPids = new Set<string>();
      (ra || []).forEach(r => allPids.add(r.player_id));
      (dl || []).forEach(r => allPids.add(r.player_id));
      pms.forEach(r => allPids.add(r.player_id));

      const pidArr = [...allPids];
      let allPlayers: Player[] = [];
      // Batch in chunks of 50 to avoid URL length limits
      for (let i = 0; i < pidArr.length; i += 50) {
        const chunk = pidArr.slice(i, i + 50);
        const { data: pData } = await supabase
          .from('players')
          .select('id, full_name, position_abbreviation, mlb_team_abbreviation')
          .in('id', chunk);
        if (pData) allPlayers = allPlayers.concat(pData as Player[]);
      }
      const pm = new Map(allPlayers.map(p => [p.id, p]));

      // 7. Opponent name
      let oppName = '';
      if (currentMatchup) {
        const oppId = currentMatchup.home_team_id === selectedTeamId
          ? currentMatchup.away_team_id
          : currentMatchup.home_team_id;
        oppName = teamNamesMap.get(oppId) || oppId.slice(0, 8);
      }

      // 8. Compute mismatches
      const mm: Mismatch[] = [];
      const raMap = new Map((ra || []).map(r => [r.player_id, r]));
      const dlMap = new Map((dl || []).map(r => [r.player_id, r]));

      // Ghost: in daily_lineups but not in roster_assignments
      for (const d of (dl || [])) {
        if (!raMap.has(d.player_id)) {
          const name = pm.get(d.player_id)?.full_name || d.player_id.slice(0, 8);
          mm.push({ type: 'ghost', player_name: name, detail: `In daily_lineups (${d.slot}) but no roster_assignment` });
        }
      }

      // Missing: in roster_assignments but not in daily_lineups for today
      for (const r of (ra || [])) {
        if (!dlMap.has(r.player_id)) {
          const name = pm.get(r.player_id)?.full_name || r.player_id.slice(0, 8);
          mm.push({ type: 'missing', player_name: name, detail: `In roster_assignments (${r.slot}) but no daily_lineups row for today` });
        }
      }

      // Slot collisions in daily_lineups
      const slotCounts = new Map<string, string[]>();
      for (const d of (dl || [])) {
        const existing = slotCounts.get(d.slot) || [];
        existing.push(d.player_id);
        slotCounts.set(d.slot, existing);
      }
      for (const [slot, pids] of slotCounts) {
        if (pids.length > 1) {
          const names = pids.map(id => pm.get(id)?.full_name || id.slice(0, 8)).join(', ');
          mm.push({ type: 'slot_collision', player_name: names, detail: `${pids.length} players in slot ${slot}` });
        }
      }

      // Starter mismatch
      for (const r of (ra || [])) {
        const d = dlMap.get(r.player_id);
        if (d && r.is_starter !== d.is_starter) {
          const name = pm.get(r.player_id)?.full_name || r.player_id.slice(0, 8);
          mm.push({
            type: 'starter_mismatch',
            player_name: name,
            detail: `RA is_starter=${r.is_starter} vs DL is_starter=${d.is_starter}`,
          });
        }
      }

      setRoster(ra || []);
      setDailyLineups(dl || []);
      setPlayerMap(pm);
      setMatchup(currentMatchup);
      setOpponentName(oppName);
      setPmsRows(pms);
      setSabotagesSent(sentSab);
      setSabotagesReceived(recvSab);
      setMismatches(mm);
    } finally {
      setLoading(false);
    }
  }, [selectedTeamId, selectedLeagueId, teamNamesMap]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // ─── Admin Chat: fetch messages for selected league + realtime ───────
  const fetchChatMessages = useCallback(async () => {
    if (!selectedLeagueId) return;
    setChatLoading(true);
    const { data } = await supabase
      .from('league_messages')
      .select('*')
      .eq('league_id', selectedLeagueId)
      .order('created_at', { ascending: true })
      .limit(100);
    if (data) setChatMessages(data);
    setChatLoading(false);
  }, [selectedLeagueId]);

  useEffect(() => {
    fetchChatMessages();
  }, [fetchChatMessages]);

  // Realtime subscription for chat messages
  useEffect(() => {
    if (!selectedLeagueId) return;
    const channel = supabase
      .channel(`admin-chat-${selectedLeagueId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'league_messages',
        filter: `league_id=eq.${selectedLeagueId}`,
      }, (payload) => {
        setChatMessages(prev => [...prev, payload.new as any]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedLeagueId]);

  // Scroll chat to bottom when messages change
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const viewport = el.closest('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [chatMessages]);

  const sendAdminMessage = async () => {
    if (!adminMessage.trim() || !user) return;
    setSendingChat(true);
    try {
      if (broadcastMode) {
        // Send to ALL leagues
        const leagueIds = leagues.map(l => l.id);
        for (const lid of leagueIds) {
          await supabase.from('league_messages').insert({
            league_id: lid,
            user_id: user.id,
            sender_name: 'Sabotage HQ',
            content: adminMessage,
            is_system: false,
            is_admin: true,
          });
        }
      } else {
        await supabase.from('league_messages').insert({
          league_id: selectedLeagueId,
          user_id: user.id,
          sender_name: 'Sabotage HQ',
          content: adminMessage,
          is_system: false,
          is_admin: true,
        });
      }
      setAdminMessage('');
      fetchChatMessages();
    } finally {
      setSendingChat(false);
    }
  };

  // ─── Derived data ─────────────────────────────────────────
  const teamPms = pmsRows.filter(r => r.team_id === selectedTeamId);
  const starters = roster.filter(r => r.is_starter && r.slot !== 'IL');
  const bench = roster.filter(r => !r.is_starter && r.slot === 'BENCH');
  const il = roster.filter(r => r.slot === 'IL');

  // SAB PTS per player from current matchup (rostered rows only)
  const sabPtsMap = new Map<string, number>();
  for (const r of teamPms) {
    sabPtsMap.set(r.player_id, (sabPtsMap.get(r.player_id) || 0) + Number(r.total_points));
  }

  // Daily lineups map for comparison
  const dlMap = new Map(dailyLineups.map(r => [r.player_id, r]));

  // ─── Guard ─────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Shield className="w-8 h-8 text-primary animate-pulse" />
      </div>
    );
  }
  if (!user || !isAdmin) return null;

  const selectedTeamName = teams.find(t => t.id === selectedTeamId)?.name || '';

  // ─── Render helpers ───────────────────────────────────────
  const pName = (pid: string) => playerMap.get(pid)?.full_name || pid.slice(0, 8);
  const pPos = (pid: string) => playerMap.get(pid)?.position_abbreviation || '?';
  const pTeam = (pid: string) => playerMap.get(pid)?.mlb_team_abbreviation || '';

  const mismatchColor = (type: Mismatch['type']) => {
    switch (type) {
      case 'ghost': return 'text-red-400';
      case 'missing': return 'text-orange-400';
      case 'slot_collision': return 'text-red-500 font-bold';
      case 'starter_mismatch': return 'text-yellow-400';
    }
  };

  const mismatchBadge = (type: Mismatch['type']) => {
    switch (type) {
      case 'ghost': return <Badge variant="destructive" className="text-[10px]">GHOST</Badge>;
      case 'missing': return <Badge className="bg-orange-500/20 text-orange-400 text-[10px]">MISSING</Badge>;
      case 'slot_collision': return <Badge variant="destructive" className="text-[10px]">COLLISION</Badge>;
      case 'starter_mismatch': return <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">STARTER</Badge>;
    }
  };

  const renderRosterSection = (label: string, rows: RosterRow[]) => {
    if (rows.length === 0) return null;
    return (
      <Card className="bg-card border-border">
        <CardHeader className="py-2 px-4">
          <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {label} ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <th className="text-left px-3 py-1.5">Player</th>
                <th className="text-left px-2 py-1.5">POS</th>
                <th className="text-left px-2 py-1.5">Team</th>
                <th className="text-left px-2 py-1.5">RA Slot</th>
                <th className="text-left px-2 py-1.5">DL Slot</th>
                <th className="text-left px-2 py-1.5">RA Start</th>
                <th className="text-left px-2 py-1.5">DL Start</th>
                <th className="text-left px-2 py-1.5">Locked</th>
                <th className="text-right px-3 py-1.5">SAB PTS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const dl = dlMap.get(r.player_id);
                const slotMismatch = dl && dl.slot !== r.slot;
                const starterMismatch = dl && dl.is_starter !== r.is_starter;
                const noDl = !dl;
                return (
                  <tr key={r.id} className={cn("border-b border-border/40", noDl && "bg-orange-500/5")}>
                    <td className="px-3 py-1.5 font-medium">{pName(r.player_id)}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{pPos(r.player_id)}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{pTeam(r.player_id)}</td>
                    <td className="px-2 py-1.5">{r.slot}</td>
                    <td className={cn("px-2 py-1.5", slotMismatch && "text-red-400 font-bold", noDl && "text-orange-400")}>
                      {dl ? dl.slot : '-'}
                    </td>
                    <td className="px-2 py-1.5">{r.is_starter ? 'Y' : 'N'}</td>
                    <td className={cn("px-2 py-1.5", starterMismatch && "text-red-400 font-bold", noDl && "text-orange-400")}>
                      {dl ? (dl.is_starter ? 'Y' : 'N') : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">
                      {dl?.locked_at ? new Date(dl.locked_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-'}
                    </td>
                    <td className={cn(
                      "px-3 py-1.5 text-right font-bold tabular-nums",
                      (sabPtsMap.get(r.player_id) || 0) > 0 ? "text-orange-400" : (sabPtsMap.get(r.player_id) || 0) < 0 ? "text-green-400" : "text-muted-foreground"
                    )}>
                      {(sabPtsMap.get(r.player_id) || 0).toFixed(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-primary" />
            <span className="font-bold text-sm">God Mode</span>
            <Badge className="bg-primary/20 text-primary text-[10px]">ADMIN</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => fetchTeamData()} disabled={loading || !selectedTeamId}>
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Ops
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Selectors */}
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 block">League</label>
            <div className="relative">
              <select
                className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm appearance-none pr-8"
                value={selectedLeagueId}
                onChange={e => setSelectedLeagueId(e.target.value)}
              >
                <option value="">Select league...</option>
                {leagues.map(l => (
                  <option key={l.id} value={l.id}>{l.name} ({l.status})</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1 block">Team</label>
            <div className="relative">
              <select
                className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm appearance-none pr-8"
                value={selectedTeamId}
                onChange={e => setSelectedTeamId(e.target.value)}
                disabled={teams.length === 0}
              >
                <option value="">Select team...</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}, {t.owner_name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>

        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {!loading && selectedTeamId && (
          <>
            {/* ─── Mismatches Summary ──────────────────────── */}
            <Card className={cn("border", mismatches.length > 0 ? "border-red-500/50 bg-red-500/5" : "border-green-500/50 bg-green-500/5")}>
              <CardHeader className="py-2 px-4">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                  {mismatches.length > 0
                    ? <><AlertTriangle className="w-4 h-4 text-red-400" /> <span className="text-red-400">{mismatches.length} Mismatch{mismatches.length !== 1 ? 'es' : ''} Found</span></>
                    : <><CheckCircle2 className="w-4 h-4 text-green-400" /> <span className="text-green-400">No Mismatches</span></>
                  }
                </CardTitle>
              </CardHeader>
              {mismatches.length > 0 && (
                <CardContent className="px-4 pb-3 pt-0">
                  <div className="space-y-1">
                    {mismatches.map((m, i) => (
                      <div key={i} className={cn("text-xs flex items-center gap-2", mismatchColor(m.type))}>
                        {mismatchBadge(m.type)}
                        <span className="font-medium">{m.player_name}</span>
                        <span className="text-muted-foreground">: {m.detail}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* ─── Current Matchup ─────────────────────────── */}
            {matchup ? (
              <Card className="bg-card border-border">
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Swords className="w-3.5 h-3.5" /> Week {matchup.week} Matchup
                    <Badge variant="outline" className="text-[10px] ml-auto">{matchup.status}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3 pt-0">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex-1">
                      <span className={cn("font-bold", matchup.home_team_id === selectedTeamId && "text-primary")}>
                        {teamNamesMap.get(matchup.home_team_id) || 'Home'}
                      </span>
                      <span className="text-lg font-bold ml-2 tabular-nums">{Number(matchup.home_score).toFixed(1)}</span>
                    </div>
                    <span className="text-muted-foreground text-xs px-3">vs</span>
                    <div className="flex-1 text-right">
                      <span className="text-lg font-bold mr-2 tabular-nums">{Number(matchup.away_score).toFixed(1)}</span>
                      <span className={cn("font-bold", matchup.away_team_id === selectedTeamId && "text-primary")}>
                        {teamNamesMap.get(matchup.away_team_id) || 'Away'}
                      </span>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {matchup.week_start} → {matchup.week_end}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="py-4 text-center text-xs text-muted-foreground">
                  No active matchup found for today
                </CardContent>
              </Card>
            )}

            {/* ─── Roster (RA vs DL comparison) ────────────── */}
            {renderRosterSection('Starters', starters)}
            {renderRosterSection('Bench', bench)}
            {renderRosterSection('Injured List', il)}

            {/* Ghost rows: in DL but not RA */}
            {(() => {
              const raPlayerIds = new Set(roster.map(r => r.player_id));
              const ghosts = dailyLineups.filter(d => !raPlayerIds.has(d.player_id));
              if (ghosts.length === 0) return null;
              return (
                <Card className="bg-red-500/5 border-red-500/30">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-xs font-semibold uppercase tracking-wider text-red-400">
                      Ghost DL Rows ({ghosts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                          <th className="text-left px-3 py-1.5">Player</th>
                          <th className="text-left px-2 py-1.5">DL Slot</th>
                          <th className="text-left px-2 py-1.5">DL Starter</th>
                          <th className="text-left px-2 py-1.5">Locked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ghosts.map(d => (
                          <tr key={d.id} className="border-b border-border/40">
                            <td className="px-3 py-1.5 text-red-400 font-medium">{pName(d.player_id)}</td>
                            <td className="px-2 py-1.5">{d.slot}</td>
                            <td className="px-2 py-1.5">{d.is_starter ? 'Y' : 'N'}</td>
                            <td className="px-2 py-1.5 text-muted-foreground">
                              {d.locked_at ? new Date(d.locked_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              );
            })()}

            {/* ─── Player Match Scores ──────────────────────── */}
            <Card className="bg-card border-border">
              <CardHeader className="py-2 px-4">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Player Match Scores: Week {matchup?.week || '?'} ({teamPms.length} rows)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {teamPms.length === 0 ? (
                  <div className="px-4 py-4 text-xs text-muted-foreground text-center">No scored rows for this team</div>
                ) : (
                  <ScrollArea className="max-h-[400px]">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-card z-10">
                        <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                          <th className="text-left px-3 py-1.5">Player</th>
                          <th className="text-left px-2 py-1.5">POS</th>
                          <th className="text-left px-2 py-1.5">Date</th>
                          <th className="text-center px-2 py-1.5">Starter</th>
                          <th className="text-right px-2 py-1.5">Trash</th>
                          <th className="text-right px-2 py-1.5">Anti</th>
                          <th className="text-right px-3 py-1.5">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamPms.map(r => (
                          <tr key={r.id} className={cn("border-b border-border/40", !r.is_starter && "opacity-50")}>
                            <td className="px-3 py-1.5 font-medium">{pName(r.player_id)}</td>
                            <td className="px-2 py-1.5 text-muted-foreground">{pPos(r.player_id)}</td>
                            <td className="px-2 py-1.5 text-muted-foreground tabular-nums">{r.game_date}</td>
                            <td className="px-2 py-1.5 text-center">
                              {r.is_starter
                                ? <CheckCircle2 className="w-3 h-3 text-green-400 inline" />
                                : <span className="text-muted-foreground">-</span>
                              }
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-orange-400">{Number(r.trash_points).toFixed(1)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-green-400">{Number(r.anti_points).toFixed(1)}</td>
                            <td className={cn(
                              "px-3 py-1.5 text-right font-bold tabular-nums",
                              Number(r.total_points) > 0 ? "text-orange-400" : Number(r.total_points) < 0 ? "text-green-400" : "text-muted-foreground"
                            )}>
                              {Number(r.total_points).toFixed(1)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* ─── Sabotages ───────────────────────────────── */}
            {(sabotagesSent.length > 0 || sabotagesReceived.length > 0) && (
              <Card className="bg-card border-border">
                <CardHeader className="py-2 px-4">
                  <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Bomb className="w-3.5 h-3.5" /> Sabotages: Week {matchup?.week || '?'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3 pt-0 space-y-3">
                  {sabotagesSent.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Sent ({sabotagesSent.length})</div>
                      {sabotagesSent.map(s => (
                        <div key={s.id} className="text-xs flex items-center gap-2 py-1 border-b border-border/30">
                          <span>{s.sabotage_icon}</span>
                          <span className="font-medium">{s.sabotage_name}</span>
                          <span className="text-muted-foreground">→ {teamNamesMap.get(s.target_team_id) || s.target_team_id.slice(0, 8)}</span>
                          <Badge variant="outline" className="text-[10px] ml-auto">{s.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  {sabotagesReceived.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Received ({sabotagesReceived.length})</div>
                      {sabotagesReceived.map(s => (
                        <div key={s.id} className="text-xs flex items-center gap-2 py-1 border-b border-border/30">
                          <span>{s.sabotage_icon}</span>
                          <span className="font-medium">{s.sabotage_name}</span>
                          <span className="text-muted-foreground">← {teamNamesMap.get(s.applied_by_team_id) || s.applied_by_team_id.slice(0, 8)}</span>
                          <Badge variant="outline" className="text-[10px] ml-auto">{s.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {!loading && !selectedTeamId && selectedLeagueId && (
          <div className="text-center py-12">
            <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Select a team to inspect</p>
          </div>
        )}

        {/* ─── Admin Chat ────────────────────────────────── */}
        {selectedLeagueId && (
          <Card className="bg-card border-border">
            <CardHeader className="py-2 px-4">
              <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <MessageCircle className="w-3.5 h-3.5" /> League Chat
                <Badge className="bg-orange-500/20 text-orange-400 text-[10px] ml-1">ADMIN</Badge>
                <span className="text-[10px] text-muted-foreground ml-auto font-normal normal-case">
                  {leagues.find(l => l.id === selectedLeagueId)?.name}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-72 px-4">
                <div className="py-2">
                  {chatLoading && chatMessages.length === 0 && (
                    <div className="text-center text-muted-foreground py-8 text-xs">Loading messages...</div>
                  )}
                  {!chatLoading && chatMessages.length === 0 && (
                    <div className="text-center text-muted-foreground py-8">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-xs">No messages in this league yet</p>
                    </div>
                  )}
                  {chatMessages.map(msg => {
                    const isSystem = msg.is_system;
                    const isAdmin = msg.is_admin;
                    const senderName = msg.sender_name || '';
                    const time = new Date(msg.created_at);

                    return (
                      <div key={msg.id} className={cn("flex gap-2 mb-2.5", isSystem && "justify-center")}>
                        {isSystem ? (
                          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs">
                            <Bomb className="w-3 h-3" />{msg.content}
                          </div>
                        ) : isAdmin ? (
                          <div className="w-full flex gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-orange-500 text-white">
                              <Shield className="w-3.5 h-3.5" />
                            </div>
                            <div className="max-w-[80%] rounded-2xl px-3 py-1.5 bg-orange-500/15 border border-orange-500/30 rounded-bl-sm">
                              <div className="flex items-center gap-1 mb-0.5">
                                <p className="text-[10px] font-bold text-orange-400">{senderName}</p>
                                <Badge className="bg-orange-500/20 text-orange-400 text-[8px] h-3.5 px-1">ADMIN</Badge>
                              </div>
                              <p className="text-sm leading-snug">{msg.content}</p>
                              <p className="text-[9px] mt-0.5 text-muted-foreground">
                                {formatDistanceToNow(time, { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-muted text-muted-foreground">
                              {senderName.charAt(0)}
                            </div>
                            <div className="max-w-[75%] rounded-2xl px-3 py-1.5 bg-muted rounded-bl-sm">
                              <p className="text-[10px] font-medium mb-0.5 opacity-70">{senderName}</p>
                              <p className="text-sm leading-snug">{msg.content}</p>
                              <p className="text-[9px] mt-0.5 text-muted-foreground">
                                {formatDistanceToNow(time, { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={chatScrollRef} />
                </div>
              </ScrollArea>
              <div className="p-3 border-t border-border space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setBroadcastMode(!broadcastMode)}
                    className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors",
                      broadcastMode
                        ? "bg-orange-500/20 text-orange-400 border border-orange-500/40"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Radio className="w-3 h-3" />
                    {broadcastMode ? 'BROADCAST ALL' : 'Broadcast'}
                  </button>
                  {broadcastMode && (
                    <span className="text-[10px] text-orange-400">
                      Will send to all {leagues.length} leagues
                    </span>
                  )}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); sendAdminMessage(); }} className="flex gap-2">
                  <Input
                    placeholder="Message as Sabotage HQ..."
                    value={adminMessage}
                    onChange={(e) => setAdminMessage(e.target.value)}
                    className="flex-1 h-9 text-sm"
                    disabled={sendingChat}
                  />
                  <Button type="submit" size="icon" className="h-9 w-9 bg-orange-500 hover:bg-orange-600" disabled={!adminMessage.trim() || sendingChat}>
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminGodMode;
